import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import {
  type CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';

/**
 * Shared state update helpers used by both `useChroniclePairing` and `useChronicleConnect`.
 */
export function useChronicleStateHelpers() {
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const setConnectionContext = useSetAtom(chronicleConnectionContextAtom);

  const clearAutoReconnect = useCallback(() => {
    setConnectionContext((prev) => ({
      ...prev,
      autoReconnectUntil: null,
      autoReconnectAttempts: 0,
    }));
  }, [setConnectionContext]);

  const setConnectionStatus = useCallback(
    (
      status: typeof CONNECTION_STATUS.SCANNING | typeof CONNECTION_STATUS.CONNECTING,
      deviceId?: string
    ) => {
      setDeviceState((prev) => ({
        ...prev,
        connectionStatus: status,
        error: null,
        ...(deviceId && { deviceId }),
      }));
    },
    [setDeviceState]
  );

  const setLinkedDevice = useCallback(
    (backendId: string | null) => {
      setConnectionContext((prev) => ({ ...prev, linkedDeviceId: backendId }));
    },
    [setConnectionContext]
  );

  const setIsLinkingDevice = useCallback(
    (value: boolean) => {
      setConnectionContext((prev) => ({ ...prev, isLinkingDevice: value }));
    },
    [setConnectionContext]
  );

  const markManualDisconnect = useCallback(() => {
    setConnectionContext((prev) => ({
      ...prev,
      lastDisconnectReason: 'manual',
      autoReconnectUntil: null,
      autoReconnectAttempts: 0,
    }));
  }, [setConnectionContext]);

  return {
    setDeviceState,
    setConnectionContext,
    clearAutoReconnect,
    setConnectionStatus,
    setLinkedDevice,
    setIsLinkingDevice,
    markManualDisconnect,
  };
}
